import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, ArrowRight, X, Heart, Lock, Crown, Sparkles, Phone, Eye, Languages, Zap, Briefcase, FileText, GraduationCap } from 'lucide-react';
import Header from '../components/Header';
import Paywall from '../components/Paywall';
import AuthPromptModal from '../components/AuthPromptModal';
import PopularInYourState from '../components/PopularInYourState';
import GovernmentJobs from './GovernmentJobs';
import useSeo from '../hooks/useSeo';
import { breadcrumbJsonLd, SITE_URL } from '../lib/seoSchemas';
import { translateBatch } from '../services/translationService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Session storage keys for scroll position and popup
const SCROLL_POSITION_KEY = 'schemes_scroll_position';
const FILTERS_EXPANDED_KEY = 'schemes_filters_expanded';
const PAYWALL_SHOWN_KEY = 'paywall_shown_this_session';
const PAYWALL_TIMER_KEY = 'paywall_timer_elapsed';

// Number of free schemes available
const FREE_SCHEMES_LIMIT = 150;

// Time in seconds before showing paywall after browsing (2 minutes)
const PAYWALL_TIMER_SECONDS = 120;

// Sector translation mapping
const SECTOR_TRANSLATION_KEYS = {
  'Agriculture': 'sectorAgriculture',
  'Health': 'sectorHealth',
  'Housing': 'sectorHousing',
  'Education': 'sectorEducation',
  'Employment': 'sectorEmployment',
  'Business & Entrepreneurship': 'sectorBusiness',
  'Women & Child': 'sectorWomenChild',
  'Social Welfare': 'sectorSocialWelfare',
  'Financial Inclusion': 'sectorFinancial',
  'Rural Development': 'sectorRural',
  'Urban Development': 'sectorUrban',
  'Skill Development': 'sectorSkill',
  'Skills & Employment': 'sectorSkillsEmployment',
  'Food Security': 'sectorFood',
  'Insurance': 'sectorInsurance',
  'Pension': 'sectorPension',
};

const Schemes = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();

  // Auth gate for non-signed-up users (declared early so useEffects below can reference)
  const isAuthenticated = !!user;
  const location = useLocation();
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [forcedAuth, setForcedAuth] = useState(false);
  const triggerAuthPrompt = () => setShowAuthPrompt(true);

  // Sub-tab state: 'schemes' or 'jobs'
  // When URL is /schemes/jobs/:jobSlug, force the jobs sub-tab on first render.
  const isJobSlugRoute = (location?.pathname || '').startsWith('/schemes/jobs/');
  const [activeSubTab, setActiveSubTab] = useState(() => (
    isJobSlugRoute ? 'jobs' : (searchParams.get('tab') || 'schemes')
  ));

  // If the URL switches to /schemes/jobs/:slug after mount, also flip to the jobs tab.
  useEffect(() => {
    if (isJobSlugRoute && activeSubTab !== 'jobs') {
      setActiveSubTab('jobs');
    }
  }, [isJobSlugRoute, activeSubTab]);

  // Sync active sub-tab with `?tab=` whenever the URL changes at runtime.
  // Without this, clicking a category tile on the Home page (which navigates to
  // `/schemes?tab=jobs&category=Railway`) only updates the URL — the component
  // is already mounted, so the first-render initializer above doesn't re-run
  // and the user stays on the Schemes tab.
  useEffect(() => {
    const wantsTab = searchParams.get('tab');
    if (!wantsTab || isJobSlugRoute) return;
    if (wantsTab !== activeSubTab) setActiveSubTab(wantsTab);
  }, [searchParams, isJobSlugRoute, activeSubTab]);

  // SEO — switch title/description based on active sub-tab
  useSeo(
    activeSubTab === 'jobs'
      ? {
          title: 'Government Jobs in India — UPSC, SSC, Railway, Banking & State PSC | KnowMySchemes',
          description: 'Aggregated, informational listing of 340+ active Indian government job notifications from UPSC, SSC, Railway, IBPS, APPSC, TSPSC, KPSC and other official portals. KnowMySchemes is NOT a recruiter — please verify every notification and apply on the official government website.',
          canonical: 'https://knowmyschemes.com/schemes?tab=jobs',
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: 'Government Jobs in India — Aggregated Notifications',
              description:
                'KnowMySchemes is a privately operated aggregator. We surface publicly available government recruitment notifications from official portals (UPSC, SSC, Railway, IBPS, APPSC, TSPSC, KPSC, etc.) for informational convenience. We do NOT process applications or charge users to view jobs. All applications must be made on the official government website linked from each listing.',
              url: `${SITE_URL}/schemes?tab=jobs`,
              isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'KnowMySchemes' },
              about: { '@type': 'Thing', name: 'Government Recruitment in India' },
              inLanguage: 'en-IN',
              isAccessibleForFree: true,
              publisher: { '@type': 'Organization', name: 'KnowMySchemes', url: SITE_URL },
            },
            breadcrumbJsonLd([
              { name: 'Home', url: `${SITE_URL}/` },
              { name: 'Schemes', url: `${SITE_URL}/schemes` },
              { name: 'Government Jobs', url: `${SITE_URL}/schemes?tab=jobs` },
            ]),
          ],
        }
      : {
          title: 'Government Welfare Schemes in India — Eligibility & Benefits | KnowMySchemes',
          description: 'Informational directory of 700+ central and state government welfare schemes — filter by state, occupation, caste, and income. KnowMySchemes is NOT a government department; verify details and submit applications on the official scheme portal.',
          canonical: 'https://knowmyschemes.com/schemes',
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: 'Government Welfare Schemes in India — Informational Directory',
              description:
                'KnowMySchemes is a privately operated portal that aggregates publicly available information about Indian central and state government welfare schemes. Eligibility, benefits, and application links are surfaced for informational convenience. Approval and application processing are solely the responsibility of the concerned government authority — please apply on the official scheme portal.',
              url: `${SITE_URL}/schemes`,
              isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'KnowMySchemes' },
              about: { '@type': 'Thing', name: 'Indian Government Welfare Schemes' },
              inLanguage: 'en-IN',
              isAccessibleForFree: true,
              publisher: { '@type': 'Organization', name: 'KnowMySchemes', url: SITE_URL },
            },
            breadcrumbJsonLd([
              { name: 'Home', url: `${SITE_URL}/` },
              { name: 'Schemes', url: `${SITE_URL}/schemes` },
            ]),
          ],
        }
  );
  
  // Paywall state
  const [showPaywall, setShowPaywall] = useState(false);
  const [eligibilityCount, setEligibilityCount] = useState(null);
  const [userSubscribed, setUserSubscribed] = useState(false);
  
  // Premium scheme view tracking state
  const [schemeViewStatus, setSchemeViewStatus] = useState({});
  const MAX_PREMIUM_VIEWS = 2;
  
  // Translation state for scheme names
  const [translatedNames, setTranslatedNames] = useState({});
  const [isTranslatingSchemes, setIsTranslatingSchemes] = useState(false);
  const translationLanguageRef = useRef(null);
  
  // Helper function to get translated scheme field
  const getTranslatedField = (scheme, field) => {
    // Check live translations first
    if (language !== 'en' && translatedNames[scheme.id]) {
      if (field === 'name' && translatedNames[scheme.id].name) {
        return translatedNames[scheme.id].name;
      }
      if (field === 'shortDescription' && translatedNames[scheme.id].shortDescription) {
        return translatedNames[scheme.id].shortDescription;
      }
    }
    
    // If language is English or no translations exist, return original
    if (language === 'en' || !scheme.translations) {
      return scheme[field];
    }
    
    // Try to get pre-existing translated field for current language
    const langTranslations = scheme.translations[language];
    if (langTranslations && langTranslations[field]) {
      return langTranslations[field];
    }
    
    // Fallback to original
    return scheme[field];
  };
  
  // Helper function to translate sector names
  const translateSector = (sector) => {
    const key = SECTOR_TRANSLATION_KEYS[sector];
    return key ? t(key) || sector : sector;
  };
  
  // Helper function to translate state names
  const translateState = (state) => {
    const STATE_TRANSLATION_KEYS = {
      'Central': 'central',
      'Andhra Pradesh': 'andhrapradesh',
      'Telangana': 'telangana',
      'Karnataka': 'karnataka'
    };
    const key = STATE_TRANSLATION_KEYS[state];
    return key ? t(key) || state : state;
  };
  
  // Helper function to translate scheme type
  const translateType = (type) => {
    if (type?.toLowerCase() === 'central') return t('central');
    if (type?.toLowerCase() === 'state') return t('stateType');
    return type;
  };
  
  // Initialize filter expanded state from session storage
  const [showFilters, setShowFilters] = useState(() => {
    const saved = sessionStorage.getItem(FILTERS_EXPANDED_KEY);
    return saved === 'true';
  });

  // Ref for scroll restoration
  const containerRef = useRef(null);
  const isRestoringScroll = useRef(false);
  
  const [schemes, setSchemes] = useState([]);
  const [filteredSchemes, setFilteredSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize filter states from URL params
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [selectedSector, setSelectedSector] = useState(() => searchParams.get('sector') || 'All');
  const [selectedCaste, setSelectedCaste] = useState(() => searchParams.get('caste') || 'All');
  const [selectedType, setSelectedType] = useState(() => searchParams.get('type') || 'All');
  const [selectedState, setSelectedState] = useState(() => searchParams.get('state') || 'All');
  const [showPremiumOnly, setShowPremiumOnly] = useState(false);
  const [savedSchemeIds, setSavedSchemeIds] = useState([]);
  
  // Fixed state options - only these 4 states are allowed
  const [states] = useState(['All', 'Central', 'Andhra Pradesh', 'Telangana', 'Karnataka']);
  const [sectors, setSectors] = useState(['All']);
  const [castes] = useState(['All', 'General', 'SC', 'ST', 'OBC']);
  const [types] = useState(['All', 'Central', 'State']);

  // Update URL with current filter state — uses ?q=query&sector=...&state=...
  // Preserves any foreign params (e.g. `tab`, `category`, `search`, `location`
  // used by the Jobs sub-tab) so deep-linking from the home-page category tiles
  // keeps working. Only scheme-filter keys are authoritative here.
  const SCHEME_FILTER_KEYS = ['q', 'sector', 'caste', 'type', 'state'];
  const updateURLParams = useCallback((query, sector, caste, type, state) => {
    const params = new URLSearchParams(window.location.search);

    // Carry over all non-scheme-filter params unchanged (tab, category, search, location, etc.)
    SCHEME_FILTER_KEYS.forEach((k) => params.delete(k));

    if (query) params.set('q', query);
    if (sector && sector !== 'All') params.set('sector', sector);
    if (caste && caste !== 'All') params.set('caste', caste);
    if (type && type !== 'All') params.set('type', type);
    if (state && state !== 'All') params.set('state', state);

    // Use replace to avoid adding to history stack for filter changes
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Update URL when any filter changes
  useEffect(() => {
    updateURLParams(searchQuery, selectedSector, selectedCaste, selectedType, selectedState);
  }, [searchQuery, selectedSector, selectedCaste, selectedType, selectedState, updateURLParams]);

  // Save filter expanded state
  useEffect(() => {
    sessionStorage.setItem(FILTERS_EXPANDED_KEY, showFilters.toString());
  }, [showFilters]);

  // Translate scheme names when language changes
  useEffect(() => {
    const translateSchemeNames = async () => {
      if (language === 'en' || filteredSchemes.length === 0) {
        setTranslatedNames({});
        translationLanguageRef.current = language;
        return;
      }
      
      // Skip if already translated for this language
      if (translationLanguageRef.current === language && Object.keys(translatedNames).length > 0) {
        return;
      }
      
      setIsTranslatingSchemes(true);
      try {
        // Only translate visible schemes (first 50 for performance)
        const visibleSchemes = filteredSchemes.slice(0, 50);
        const names = visibleSchemes.map(s => s.name || '');
        const descriptions = visibleSchemes.map(s => s.shortDescription || s.description?.substring(0, 100) || '');
        
        const [translatedNamesList, translatedDescList] = await Promise.all([
          translateBatch(names, language, 'en'),
          translateBatch(descriptions, language, 'en')
        ]);
        
        // Create translation map
        const translations = {};
        visibleSchemes.forEach((scheme, index) => {
          translations[scheme.id] = {
            name: translatedNamesList[index] || names[index],
            shortDescription: translatedDescList[index] || descriptions[index]
          };
        });
        
        setTranslatedNames(translations);
        translationLanguageRef.current = language;
      } catch (error) {
        console.error('Scheme name translation error:', error);
      } finally {
        setIsTranslatingSchemes(false);
      }
    };
    
    translateSchemeNames();
  }, [filteredSchemes, language]);

  // Save scroll position before navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      const scrollY = window.scrollY;
      sessionStorage.setItem(SCROLL_POSITION_KEY, scrollY.toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // Save scroll position when component unmounts (navigation)
      const scrollY = window.scrollY;
      sessionStorage.setItem(SCROLL_POSITION_KEY, scrollY.toString());
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && filteredSchemes.length > 0 && !isRestoringScroll.current) {
      const savedScrollY = sessionStorage.getItem(SCROLL_POSITION_KEY);
      if (savedScrollY) {
        isRestoringScroll.current = true;
        // Small delay to ensure DOM is rendered
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedScrollY, 10));
          // Clear after restoring to prevent issues on subsequent filter changes
          setTimeout(() => {
            sessionStorage.removeItem(SCROLL_POSITION_KEY);
            isRestoringScroll.current = false;
          }, 100);
        });
      }
    }
  }, [loading, filteredSchemes.length]);

  useEffect(() => {
    const fetchSavedIds = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/saved-schemes/ids`);
        setSavedSchemeIds(response.data);
      } catch (error) {
        console.error('Error fetching saved schemes:', error);
      }
    };
    fetchSavedIds();
  }, []);

  // Fetch subscription status and eligibility count
  useEffect(() => {
    const fetchUserStatus = async () => {
      if (!user) return;
      
      try {
        // Check subscription status
        const statusRes = await axios.get(`${BACKEND_URL}/api/subscription/status?user_id=${user.id}`);
        setUserSubscribed(statusRes.data.is_subscribed);
        
        // Get eligibility count
        const eligibilityRes = await axios.get(`${BACKEND_URL}/api/subscription/eligibility-count?user_id=${user.id}`);
        setEligibilityCount(eligibilityRes.data);
        
        // Fetch premium scheme view status (requires auth)
        const token = localStorage.getItem('token');
        const viewStatusRes = await axios.get(
          `${BACKEND_URL}/api/schemes/all-view-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSchemeViewStatus(viewStatusRes.data.view_status || {});
      } catch (error) {
        console.error('Error fetching user status:', error);
      }
    };
    fetchUserStatus();
  }, [user]);

  // Auth popup for non-signed-up users — trigger after scrolling past 60% of page
  useEffect(() => {
    if (isAuthenticated || showAuthPrompt) return;
    // sessionStorage flag so it doesn't pop repeatedly on the same session
    if (sessionStorage.getItem('schemes_auth_prompt_shown') === '1') return;
    let triggered = false;
    const onScroll = () => {
      if (triggered) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      // Trigger when user has scrolled past 50% of total page height
      if (total > 0 && scrolled / total > 0.5) {
        triggered = true;
        setShowAuthPrompt(true);
        try { sessionStorage.setItem('schemes_auth_prompt_shown', '1'); } catch (_e) { /* sessionStorage disabled — non-blocking */ }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isAuthenticated, showAuthPrompt]);

  // FORCED auth popup for guests after 1 minute on Schemes page (no cancel option)
  useEffect(() => {
    if (isAuthenticated) return;
    let elapsedMs = 0;
    let lastTick = Date.now();
    let intervalId = null;

    const tick = () => {
      // Only count time when tab is visible
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        elapsedMs += now - lastTick;
        lastTick = now;
        if (elapsedMs >= 60_000) {
          setForcedAuth(true);
          setShowAuthPrompt(true);
          if (intervalId) clearInterval(intervalId);
        }
      } else {
        lastTick = Date.now();
      }
    };

    const onVisibility = () => {
      lastTick = Date.now();
    };

    intervalId = setInterval(tick, 1000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated]);

  // Automatic paywall popup on first visit per session (1 second delay) — auth users only
  useEffect(() => {
    if (!isAuthenticated) return;
    const hasSeenPaywall = sessionStorage.getItem(PAYWALL_SHOWN_KEY);
    if (!hasSeenPaywall && !userSubscribed && !loading && schemes.length > 0) {
      // Small delay to ensure page is rendered
      const timer = setTimeout(() => {
        setShowPaywall(true);
        sessionStorage.setItem(PAYWALL_SHOWN_KEY, 'true');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, userSubscribed, loading, schemes.length]);

  // Timed paywall trigger - shows after 2 minutes on page (with tab visibility handling)
  useEffect(() => {
    // Skip if not authenticated, subscribed, or paywall already shown
    if (!isAuthenticated || userSubscribed || showPaywall || loading) return;
    
    let elapsedTime = 0;
    let intervalId = null;
    let isTabActive = !document.hidden;
    
    // Start or resume the timer
    const startTimer = () => {
      if (intervalId) return; // Already running
      
      intervalId = setInterval(() => {
        elapsedTime += 1;
        
        // Check if 2 minutes have passed
        if (elapsedTime >= PAYWALL_TIMER_SECONDS) {
          const hasSeenPaywall = sessionStorage.getItem(PAYWALL_SHOWN_KEY);
          if (!hasSeenPaywall && !userSubscribed) {
            setShowPaywall(true);
            sessionStorage.setItem(PAYWALL_SHOWN_KEY, 'true');
          }
          // Clear interval after showing popup
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      }, 1000);
    };
    
    // Pause the timer
    const pauseTimer = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    
    // Handle visibility change (tab switch)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - pause timer
        isTabActive = false;
        pauseTimer();
      } else {
        // Tab is visible - resume timer
        isTabActive = true;
        const hasSeenPaywall = sessionStorage.getItem(PAYWALL_SHOWN_KEY);
        if (!hasSeenPaywall && !userSubscribed && !showPaywall) {
          startTimer();
        }
      }
    };
    
    // Start timer if tab is active and paywall hasn't been shown
    const hasSeenPaywall = sessionStorage.getItem(PAYWALL_SHOWN_KEY);
    if (isTabActive && !hasSeenPaywall) {
      startTimer();
    }
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, userSubscribed, showPaywall, loading]);

  // Auth gate for non-signed-up users
  const handleToggleSave = async (schemeId, e) => {
    e.stopPropagation();
    if (!isAuthenticated) { triggerAuthPrompt(); return; }
    try {
      if (savedSchemeIds.includes(schemeId)) {
        await axios.delete(`${BACKEND_URL}/api/saved-schemes/unsave/${schemeId}`);
        setSavedSchemeIds(prev => prev.filter(id => id !== schemeId));
      } else {
        await axios.post(`${BACKEND_URL}/api/saved-schemes/save`, { scheme_id: schemeId });
        setSavedSchemeIds(prev => [...prev, schemeId]);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        setLoading(true);
        // Use new API with access info
        const response = await axios.get(`${BACKEND_URL}/api/schemes/with-access`, {
          params: { user_subscribed: userSubscribed }
        });
        
        const data = response.data;
        setSchemes(data.schemes || []);
        setFilteredSchemes(data.schemes || []);
        
        // Only extract unique sectors (states are fixed)
        const uniqueSectors = ['All', ...new Set((data.schemes || []).map(s => s.sector))];
        setSectors(uniqueSectors);
      } catch (error) {
        console.error('Error fetching schemes:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchemes();
  }, [userSubscribed]);

  useEffect(() => {
    let filtered = schemes;
    
    if (searchQuery) {
      filtered = filtered.filter(scheme => 
        scheme.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        scheme.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        scheme.shortDescription.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (selectedState !== 'All') {
      filtered = filtered.filter(scheme => scheme.state === selectedState);
    }
    
    if (selectedSector !== 'All') {
      filtered = filtered.filter(scheme => scheme.sector === selectedSector);
    }
    
    if (selectedCaste !== 'All') {
      filtered = filtered.filter(scheme => 
        scheme.caste.includes('All') || scheme.caste.includes(selectedCaste)
      );
    }
    
    if (selectedType !== 'All') {
      filtered = filtered.filter(scheme => 
        scheme.type.toLowerCase() === selectedType.toLowerCase()
      );
    }

    if (showPremiumOnly) {
      filtered = filtered.filter(scheme => scheme.is_premium === true);
    }
    
    setFilteredSchemes(filtered);
  }, [schemes, searchQuery, selectedState, selectedSector, selectedCaste, selectedType, showPremiumOnly]);

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedSector('All');
    setSelectedCaste('All');
    setSelectedType('All');
    setSelectedState('All');
    setShowPremiumOnly(false);
    // Clear URL params
    setSearchParams({}, { replace: true });
    // Clear saved scroll position
    sessionStorage.removeItem(SCROLL_POSITION_KEY);
  };

  // Track premium scheme view
  const trackSchemeView = async (schemeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/schemes/track-view`, 
        { scheme_id: schemeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local view status
      setSchemeViewStatus(prev => ({
        ...prev,
        [schemeId]: {
          view_count: response.data.view_count || (prev[schemeId]?.view_count || 0) + 1,
          remaining_views: response.data.remaining_views,
          is_blocked: response.data.is_blocked
        }
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error tracking scheme view:', error);
      return null;
    }
  };

  // Get view status for a scheme
  const getSchemeViewStatus = (schemeId) => {
    const status = schemeViewStatus[schemeId];
    if (!status) {
      return { view_count: 0, remaining_views: MAX_PREMIUM_VIEWS, is_blocked: false };
    }
    return status;
  };

  // Navigate to scheme details while preserving current URL params
  const handleViewScheme = async (scheme, index, e) => {
    if (e) e.stopPropagation();

    // Non-signed-up users always see the auth popup on any card click
    if (!isAuthenticated) {
      triggerAuthPrompt();
      return;
    }

    // Premium schemes - check view status for subscribed users
    if (scheme.is_locked || scheme.is_premium) {
      if (userSubscribed) {
        // Check if user has exhausted views
        const viewStatus = getSchemeViewStatus(scheme.id);
        
        if (viewStatus.is_blocked) {
          // User has used all views - show message
          alert(t('noViewsRemaining') || 'You have used all your views for this scheme. Please contact our team for assistance.');
          return;
        }
        
        // Navigate to scheme details - view will be tracked there
        sessionStorage.setItem(SCROLL_POSITION_KEY, window.scrollY.toString());
        navigate(`/schemes/${scheme.slug || scheme.id}`);
        return;
      } else {
        // Non-subscribed users see paywall for premium schemes
        setShowPaywall(true);
        return;
      }
    }
    
    // Only FREE schemes are accessible - navigate to details
    // Save current scroll position before navigating
    sessionStorage.setItem(SCROLL_POSITION_KEY, window.scrollY.toString());
    // Navigate to scheme details - browser back will restore the URL with params
    navigate(`/schemes/${scheme.slug || scheme.id}`);
  };

  const activeFiltersCount = [selectedState, selectedCaste, selectedType].filter(f => f !== 'All').length + (searchQuery ? 1 : 0);

  // Show active filters as chips
  const activeFilters = [];
  if (searchQuery) activeFilters.push({ key: 'search', label: `"${searchQuery}"`, clear: () => setSearchQuery('') });
  if (selectedState !== 'All') activeFilters.push({ key: 'state', label: selectedState, clear: () => setSelectedState('All') });
  if (selectedCaste !== 'All') activeFilters.push({ key: 'caste', label: selectedCaste, clear: () => setSelectedCaste('All') });
  if (selectedType !== 'All') activeFilters.push({ key: 'type', label: selectedType, clear: () => setSelectedType('All') });

  // Separate free and premium schemes based on API flag and ensure correct order
  const freeSchemes = filteredSchemes.filter(s => !s.is_locked);
  const premiumSchemes = filteredSchemes.filter(s => s.is_locked);
  // Always show free schemes first, then premium (locked) schemes
  const orderedSchemes = [...freeSchemes, ...premiumSchemes];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100" ref={containerRef}>
      {/* Shared Header */}
      <Header savedSchemeIds={savedSchemeIds} />

      {/* Hero Section - Bento Grid Design */}
      <div className="relative bg-gradient-to-br from-[#d4e6d3] via-[#dceadb] to-[#e2ede1] overflow-hidden">

        <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-center md:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-green-950 mb-2 leading-tight tracking-tight">
                {t('discoverYourBenefits')}
              </h1>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-green-700 mb-5 leading-tight tracking-tight">
                {t('getWhatYouDeserve')}
              </h2>
              
              <p className="text-gray-600 text-sm sm:text-base md:text-lg mb-6 max-w-lg mx-auto md:mx-0 leading-relaxed">
                Get your benefits — welfare schemes, government jobs, health insurance, education scholarships and women empowerment programmes.
              </p>

              {/* Schemes count badge */}
              <div className="inline-flex items-center gap-2 bg-green-100/80 text-green-800 px-5 py-2.5 rounded-full text-sm font-medium mb-6 backdrop-blur-sm border border-green-200/60">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                1000+ active schemes for both citizens and farmers
              </div>

              {/* Search CTA Button */}
              <div className="flex justify-center md:justify-start">
                <Button 
                  size="lg"
                  className="bg-green-800 hover:bg-green-700 text-white font-bold text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-6 rounded-full shadow-xl hover:shadow-2xl transition-all"
                  onClick={() => document.getElementById('schemes-list').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="explore-schemes-btn"
                >
                  SEARCH YOUR SCHEMES & JOBS
                  <Search className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right - Bento Grid */}
            <div className="hidden md:block">
              <div className="grid grid-cols-5 grid-rows-4 gap-3 h-[420px]">
                {/* JOBS Card - Large, top-left spanning 3 cols 2 rows */}
                <div className="col-span-3 row-span-2 bg-white rounded-2xl border border-green-200/60 shadow-lg p-5 flex flex-col justify-between relative overflow-hidden group hover:shadow-xl transition-shadow">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      Live
                    </div>
                  </div>
                  <div>
                    <Briefcase className="w-10 h-10 text-green-700 mb-2" />
                    <h3 className="text-xl font-extrabold text-green-900 tracking-tight">JOBS</h3>
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed">UPSC, SSC, Railway, Banking,<br/>State PSC & 340+ listings.</p>
                  </div>
                </div>

                {/* FARMERS Card - top-right spanning 2 cols 1 row */}
                <div className="col-span-2 row-span-1 bg-white rounded-2xl border border-green-200/60 shadow-lg overflow-hidden relative group hover:shadow-xl transition-shadow">
                  <img src="https://images.unsplash.com/photo-1774504798106-8c95d4278aa0?w=300&h=200&fit=crop" alt="Farmer" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent"></div>
                  <div className="relative h-full flex flex-col justify-end p-3">
                    <p className="text-xs font-extrabold text-green-900 uppercase tracking-wider">Farmers</p>
                  </div>
                </div>

                {/* WOMEN EMPOWERMENT Card - mid-right spanning 2 cols 1 row */}
                <div className="col-span-2 row-span-1 bg-white rounded-2xl border border-green-200/60 shadow-lg overflow-hidden relative group hover:shadow-xl transition-shadow">
                  <img src="https://images.unsplash.com/photo-1609252285522-ed0ebdd43551?w=300&h=200&fit=crop" alt="Women Empowerment" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent"></div>
                  <div className="relative h-full flex flex-col justify-end p-3">
                    <p className="text-[10px] font-extrabold text-green-900 uppercase tracking-wider">Women Empowerment</p>
                  </div>
                </div>

                {/* HEALTH Card - bottom-right spanning 2 cols 1 row */}
                <div className="col-span-2 row-span-1 bg-white rounded-2xl border border-green-200/60 shadow-lg overflow-hidden relative group hover:shadow-xl transition-shadow">
                  <img src="https://images.unsplash.com/photo-1659353888906-adb3e0041693?w=300&h=200&fit=crop" alt="Health" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-transparent"></div>
                  <div className="relative h-full flex flex-col justify-end p-3">
                    <p className="text-xs font-extrabold text-green-900 uppercase tracking-wider">Health</p>
                  </div>
                </div>

                {/* EDUCATION & SCHOLARSHIPS Card - bottom spanning 3 cols 2 rows */}
                <div className="col-span-3 row-span-2 bg-white rounded-2xl border border-green-200/60 shadow-lg overflow-hidden relative group hover:shadow-xl transition-shadow">
                  <img src="https://images.unsplash.com/flagged/photo-1574097656146-0b43b7660cb6?w=500&h=300&fit=crop" alt="Education" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/60 to-transparent"></div>
                  <div className="relative h-full flex flex-col justify-center p-5">
                    <GraduationCap className="w-8 h-8 text-green-700 mb-2" />
                    <h3 className="text-base font-extrabold text-green-900 uppercase tracking-wider">Education &<br/>Scholarships</h3>
                    <p className="text-gray-600 text-xs mt-1">Find scholarships and educational support.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="schemes-list" className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Sub-tabs: Schemes / Government Jobs */}
        <div className="flex mb-5 bg-white rounded-xl shadow-md p-1 border border-gray-200" data-testid="schemes-subtabs">
          <button
            onClick={() => setActiveSubTab('schemes')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeSubTab === 'schemes'
                ? 'bg-green-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="subtab-schemes"
          >
            <FileText className="w-4 h-4" />
            {t('schemes')}
          </button>
          <button
            onClick={() => setActiveSubTab('jobs')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeSubTab === 'jobs'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="subtab-jobs"
          >
            <Briefcase className="w-4 h-4" />
            {t('governmentJobs')}
          </button>
        </div>

        {/* Conditional content based on active sub-tab */}
        {activeSubTab === 'jobs' ? (
          <GovernmentJobs />
        ) : (
        <>
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <Input
              placeholder={t('searchSchemes')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 sm:pl-10 h-10 sm:h-11 text-sm sm:text-base border-green-300 focus:border-green-500 focus:ring-green-500"
              data-testid="search-schemes-input"
            />
          </div>
        </div>

        {/* Filter Toggle */}
        <div className="mb-4 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="border-green-600 text-green-700 text-sm"
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-1.5" />
            {t('filters')}
            {activeFiltersCount > 0 && (
              <Badge className="ml-1.5 bg-green-600 text-white text-xs px-1.5 py-0">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
          
          {activeFiltersCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={resetFilters}
              className="text-green-700 text-sm"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              {t('clear')}
            </Button>
          )}
        </div>

        {/* Active Filter Chips */}
        {(activeFilters.length > 0 || showPremiumOnly) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {showPremiumOnly && (
              <Badge 
                variant="secondary"
                className="bg-orange-100 text-orange-800 hover:bg-orange-200 cursor-pointer pl-3 pr-1 py-1 text-xs sm:text-sm flex items-center gap-1"
                onClick={() => setShowPremiumOnly(false)}
                data-testid="premium-filter-chip"
              >
                <Crown className="w-3 h-3 mr-1" /> Premium
                <X className="w-3 h-3 ml-1 hover:text-orange-900" />
              </Badge>
            )}
            {activeFilters.map((filter) => (
              <Badge 
                key={filter.key}
                variant="secondary"
                className="bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer pl-3 pr-1 py-1 text-xs sm:text-sm flex items-center gap-1"
                onClick={filter.clear}
              >
                {filter.label}
                <X className="w-3 h-3 ml-1 hover:text-green-900" />
              </Badge>
            ))}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <Card className="p-4 sm:p-5 md:p-6 mb-4 border border-gray-200 shadow-lg bg-white rounded-xl" data-testid="filters-panel">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* State Filter - labeled as Type */}
              <div className="space-y-1.5">
                <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  {t('type')}
                </label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger className="h-10 sm:h-11 text-sm bg-white border-gray-300 hover:border-green-400 shadow-sm" data-testid="state-filter">
                    <SelectValue placeholder={t('selectState')} />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state} value={state}>{state === 'All' ? t('all') : state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter */}
              <div className="space-y-1.5">
                <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  {t('category')}
                </label>
                <Select value={selectedCaste} onValueChange={setSelectedCaste}>
                  <SelectTrigger className="h-10 sm:h-11 text-sm bg-white border-gray-300 hover:border-green-400 shadow-sm" data-testid="caste-filter">
                    <SelectValue placeholder={t('selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {castes.map((caste) => (
                      <SelectItem key={caste} value={caste}>{caste === 'All' ? t('all') : caste}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Premium Filter */}
              <div className="space-y-1.5">
                <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Crown className="w-3 h-3 text-orange-500" />
                  Premium
                </label>
                <button
                  onClick={() => setShowPremiumOnly(!showPremiumOnly)}
                  className={`w-full h-10 sm:h-11 text-sm rounded-md border px-3 flex items-center justify-center gap-2 transition ${
                    showPremiumOnly
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                  }`}
                  data-testid="premium-filter-btn"
                >
                  <Crown className="w-4 h-4" />
                  {showPremiumOnly ? 'Showing Premium' : 'Show Premium'}
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Eligibility Count Banner */}
        {showPremiumOnly && (
          <div className="mb-4 p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg" data-testid="premium-banner">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-600 flex-shrink-0" />
              <p className="text-sm text-orange-800">
                These schemes were discovered by KMS Smart AI. Subscribe to access full details.
              </p>
            </div>
          </div>
        )}
        {eligibilityCount && (
          <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 mb-6" data-testid="eligibility-banner">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6" />
                <div>
                  <p className="font-semibold">{t('youAreEligibleFor')} {eligibilityCount.total_eligible} {t('schemes')}!</p>
                  <p className="text-sm text-purple-100">{eligibilityCount.free_schemes} {t('free')} + {eligibilityCount.premium_schemes} {t('premium')}</p>
                </div>
              </div>
              {!userSubscribed && (
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-white text-purple-700 hover:bg-purple-50"
                  size="sm"
                  data-testid="get-assistance-btn"
                >
                  <Crown className="w-4 h-4 mr-1" />
                  {t('getAssistance')}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Results Count */}
        <div className="mb-4 sm:mb-6 flex flex-wrap items-center justify-between gap-2">
          <p className="text-gray-700 text-sm sm:text-base md:text-lg" data-testid="results-count">
            {t('found')} <span className="font-bold text-green-700">{freeSchemes.length}</span> {t('free')} + <span className="font-bold text-purple-700">{premiumSchemes.length}</span> {t('premium')} {t('schemes')}
          </p>
          {premiumSchemes.length > 0 && !userSubscribed && (
            <Badge className="bg-purple-100 text-purple-700">
              <Lock className="w-3 h-3 mr-1" />
              {premiumSchemes.length} {t('locked')}
            </Badge>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12 sm:py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-16 sm:w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-sm sm:text-lg">{t('loadingSchemes')}</p>
            </div>
          </div>
        ) : orderedSchemes.length > 0 ? (
          <>
            {/* Popular in your state — only when no state filter is active */}
            {selectedState === 'All' && !searchQuery && selectedSector === 'All' && (
              <PopularInYourState
                currentUser={user}
                onApplyStateFilter={(st) => {
                  setSelectedState(st);
                  // Scroll to results so user sees the filter took effect
                  setTimeout(() => window.scrollTo({ top: 400, behavior: 'smooth' }), 50);
                }}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6" data-testid="schemes-grid">
            {orderedSchemes.map((scheme, index) => {
              // Premium schemes are ALWAYS locked (blurred) - subscription is for assistance service
              // The is_locked flag from API indicates if scheme is premium
              const isLocked = scheme.is_locked;
              // Subscribed users can see full details but scheme still shows locked visual
              const isSubscribedPremium = userSubscribed && scheme.is_locked;
              
              // Get view status for premium schemes
              const viewStatus = getSchemeViewStatus(scheme.id);
              const remainingViews = viewStatus.remaining_views;
              const isViewExhausted = viewStatus.is_blocked;
              
              return (
                <Card 
                  key={scheme.id} 
                  className={`relative overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-none shadow-md group ${
                    isLocked ? 'bg-gray-100' : 'bg-white'
                  }`}
                  onClick={(e) => handleViewScheme(scheme, index, e)}
                  data-testid={`scheme-card-${index}`}
                >
                  {/* Locked Premium Card - Shows for both subscribed and non-subscribed users */}
                  {isLocked ? (
                    <div className="relative">
                      {/* Blurred Background Content - More blur if views exhausted */}
                      <div className={`p-4 sm:p-5 md:p-6 select-none pointer-events-none ${
                        isSubscribedPremium && isViewExhausted ? 'filter blur-lg' : 'filter blur-md'
                      }`}>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                          <Badge className="bg-gradient-to-r from-gray-400 to-gray-500 text-white text-xs sm:text-sm">
                            Premium
                          </Badge>
                          <Badge className="bg-gradient-to-r from-gray-400 to-gray-500 text-white text-xs sm:text-sm">
                            Scheme
                          </Badge>
                        </div>
                        <h3 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 leading-tight text-gray-600">
                          Premium Government Scheme Name Here
                        </h3>
                        <p className="mb-3 sm:mb-4 text-xs sm:text-sm md:text-base text-gray-500">
                          This premium scheme offers significant benefits for eligible citizens. Subscribe to unlock details.
                        </p>
                        <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                          <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <span className="font-semibold text-gray-500">Sector:</span>
                            <Badge variant="outline" className="text-xs sm:text-sm border-gray-300 text-gray-500">Category</Badge>
                          </div>
                        </div>
                      </div>
                      
                      {/* Lock Overlay - Different message for subscribed vs non-subscribed */}
                      <div className={`absolute inset-0 flex flex-col items-center justify-center p-4 text-center ${
                        isSubscribedPremium && isViewExhausted 
                          ? 'bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-900/90' 
                          : 'bg-gradient-to-br from-purple-900/80 via-indigo-900/70 to-purple-900/80'
                      }`}>
                        <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm ${
                          isSubscribedPremium && isViewExhausted ? 'bg-gray-500/30' : 'bg-white/20'
                        }`}>
                          <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                          {t('premiumScheme')}
                        </h3>
                        {isSubscribedPremium ? (
                          isViewExhausted ? (
                            // Views exhausted - show blurred message
                            <>
                              <p className="text-gray-300 text-sm mb-3 max-w-[220px]">
                                {t('viewsExhausted') || 'You have used all your views for this scheme'}
                              </p>
                              <div className="bg-red-500/20 border border-red-400/50 rounded-lg px-4 py-2 mb-4">
                                <p className="text-red-200 text-xs font-medium">
                                  0 {t('viewsRemaining') || 'views remaining'}
                                </p>
                              </div>
                              <Button 
                                className="bg-gray-600 text-white hover:bg-gray-700 font-semibold"
                                size="sm"
                                disabled
                              >
                                <Phone className="w-4 h-4 mr-1.5" />
                                {t('contactTeam') || 'Contact Team'}
                              </Button>
                            </>
                          ) : (
                            // Has remaining views - show view count
                            <>
                              <p className="text-purple-100 text-sm mb-2 max-w-[220px]">
                                {t('clickToView') || 'Click to view scheme details'}
                              </p>
                              <div className="bg-green-500/20 border border-green-400/50 rounded-lg px-4 py-2 mb-4">
                                <p className="text-green-200 text-sm font-semibold">
                                  {remainingViews} {t('viewsRemaining') || 'views remaining'}
                                </p>
                              </div>
                              <Button 
                                className="bg-green-500 text-white hover:bg-green-600 font-semibold"
                                size="sm"
                                data-testid={`view-premium-scheme-btn-${index}`}
                              >
                                <Eye className="w-4 h-4 mr-1.5" />
                                {t('viewDetails') || 'View Details'}
                              </Button>
                            </>
                          )
                        ) : (
                          <>
                            <p className="text-purple-100 text-sm mb-4 max-w-[200px]">
                              {t('unlockHighValueSchemes')} ({premiumSchemes.length}+)
                            </p>
                            <Button 
                              className="bg-white text-purple-700 hover:bg-purple-50 font-semibold"
                              size="sm"
                              data-testid={`unlock-scheme-btn-${index}`}
                            >
                              <Crown className="w-4 h-4 mr-1.5" />
                              {t('unlockNow')}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Regular Free Scheme Card */
                    <div className="p-4 sm:p-5 md:p-6">
                      {/* Save Button */}
                      <button
                        onClick={(e) => handleToggleSave(scheme.id, e)}
                        className={`absolute top-3 sm:top-4 right-3 sm:right-4 p-1.5 sm:p-2 rounded-full transition-all duration-200 z-10 ${
                          savedSchemeIds.includes(scheme.id)
                            ? 'bg-pink-100 text-pink-600'
                            : 'bg-gray-100 text-gray-400 hover:text-pink-500'
                        }`}
                        data-testid={`save-scheme-btn-${index}`}
                      >
                        <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${savedSchemeIds.includes(scheme.id) ? 'fill-current' : ''}`} />
                      </button>

                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 pr-10 sm:pr-12">
                        {scheme.source === 'kms_smart_discovery' && (
                          <Badge className="bg-gradient-to-r from-orange-400 to-amber-500 text-white text-xs" data-testid={`discovery-badge-${scheme.id}`}>
                            <Zap className="w-3 h-3 mr-1" /> NEW
                          </Badge>
                        )}
                        <Badge 
                          className={`text-xs sm:text-sm ${
                            scheme.state === 'Andhra Pradesh' 
                              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                              : scheme.state === 'Telangana'
                              ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                              : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                          }`}
                        >
                          {translateState(scheme.state)}
                        </Badge>
                        <Badge 
                          className={`text-xs sm:text-sm ${
                            scheme.type === 'state' 
                              ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                              : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                          }`}
                        >
                          {translateType(scheme.type)}
                        </Badge>
                      </div>

                      <h3 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 leading-tight text-slate-800 group-hover:text-green-700 transition-colors line-clamp-2">
                        {getTranslatedField(scheme, 'name')}
                      </h3>
                      <p className="text-gray-600 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3 text-xs sm:text-sm md:text-base">
                        {getTranslatedField(scheme, 'shortDescription')}
                      </p>

                      <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                          <span className="font-semibold text-gray-700">{t('sector')}:</span>
                          <Badge variant="outline" className="text-xs sm:text-sm border-green-300 text-green-700">{translateSector(scheme.sector)}</Badge>
                        </div>
                      </div>

                      <Button 
                        className="w-full text-xs sm:text-sm py-2 sm:py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-md"
                        data-testid={`view-scheme-btn-${index}`}
                      >
                        {t('viewDetails')}
                        <ArrowRight className="ml-1.5 sm:ml-2 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
          </>
        ) : (
          <Card className="p-8 sm:p-12 text-center border-none bg-white shadow-xl" data-testid="no-schemes-found">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 text-slate-800">{t('noSchemesFound')}</h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {t('tryAdjusting')}
              </p>
              <Button 
                onClick={resetFilters} 
                className="bg-gradient-to-r from-green-600 to-green-700 text-white"
                data-testid="clear-all-filters-btn"
              >
                {t('clearAllFilters')}
              </Button>
            </div>
          </Card>
        )}
      </>
      )}
      </div>

      {/* Paywall Modal */}
      {showPaywall && (
        <Paywall 
          onClose={() => setShowPaywall(false)} 
          premiumCount={premiumSchemes.length}
          isAutoPopup={true}
        />
      )}

      {/* Auth Prompt Modal — shown for non-signed-up users on card clicks, save, or scroll past free schemes */}
      <AuthPromptModal
        isOpen={showAuthPrompt}
        onClose={() => { if (!forcedAuth) setShowAuthPrompt(false); }}
        forced={forcedAuth}
      />
    </div>
  );
};

export default Schemes;
